/**
 * Custom Repeat Input Component
 * For rituals with custom repeat frequency (e.g., every 7 days, every 2 weeks)
 */

import { ChevronDown } from "lucide-react"
import { useCallback, useRef } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { useWatch } from "react-hook-form"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib"

export type RepeatAtType = "DAYS" | "WEEKS" | "MONTHS" | "HOURS" | "DATE"

export interface RepeatAtTypeOption {
  value: RepeatAtType
  label: string
}

export const repeatAtTypeOptions: RepeatAtTypeOption[] = [
  { value: "DAYS", label: "Days" },
  { value: "WEEKS", label: "Weeks" },
  { value: "MONTHS", label: "Months" },
  { value: "HOURS", label: "Hours" },
  { value: "DATE", label: "Specific Date" },
]

interface CustomRepeatInputProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>
  name?: FieldPath<TFieldValues>
  typeName?: FieldPath<TFieldValues>
  disabled?: boolean
  error?: string
  onClearError?: () => void
}

export function CustomRepeatInput<TFieldValues extends FieldValues = FieldValues>({
  control,
  name = "repeatAt" as FieldPath<TFieldValues>,
  typeName = "repeatAtType" as FieldPath<TFieldValues>,
  disabled = false,
  error,
  onClearError,
}: CustomRepeatInputProps<TFieldValues>) {
  // Watch the repeatAtType to determine input type
  const repeatAtTypeValue = useWatch({
    control,
    name: typeName,
  })

  const repeatAtType = (repeatAtTypeValue || "DAYS") as RepeatAtType
  const isDateType = repeatAtType === "DATE"

  // Ref for date input to programmatically open picker
  const dateInputRef = useRef<HTMLInputElement>(null)

  // Extract registration props OUTSIDE the conditional rendering
  // This ensures refs are stable and properly set
  // Destructure to get the ref separately so we can combine it with our custom ref
  const { ref: valueFormRef, ...valueRegistrationProps } = control.register(name, {
    onChange: (_e: React.ChangeEvent<HTMLInputElement>) => {
      if (error && onClearError) {
        onClearError()
      }
    },
  })

  const { ref: typeFormRef, ...typeRegistrationProps } = control.register(typeName, {
    onChange: () => {
      if (error && onClearError) {
        onClearError()
      }
    },
  })

  // Handle click on date input wrapper to open picker
  const handleDateClick = useCallback(() => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker()
    }
  }, [])

  return (
    <div className="flex gap-3">
      {/* Value Input */}
      <div className="flex-1 space-y-2">
        <Label
          className={cn(
            "text-sm uppercase",
            error ? "text-brand-terracotta-bright" : "text-primary",
          )}
        >
          {isDateType ? "Date" : "Every"}
        </Label>
        {isDateType ? (
          <div className="relative cursor-pointer" onClick={handleDateClick}>
            <input
              type="date"
              disabled={disabled}
              {...valueRegistrationProps}
              ref={e => {
                // Set the date picker ref for programmatic picker access
                dateInputRef.current = e
                // Call the form ref so react-hook-form tracks the value
                valueFormRef(e)
              }}
              className={cn(
                "w-full border-b border-input bg-transparent py-2 text-sm font-semibold focus:border-primary focus:outline-none cursor-pointer",
                error
                  ? "border-brand-terracotta-bright text-brand-terracotta-bright"
                  : "border-input",
                disabled && "opacity-50 cursor-not-allowed",
              )}
            />
          </div>
        ) : (
          // Number input for other types
          <input
            type="number"
            disabled={disabled}
            {...valueRegistrationProps}
            ref={valueFormRef}
            min="1"
            step="1"
            placeholder="Enter number"
            className={cn(
              "w-full pb-2 pt-1 border-0 border-b rounded-none bg-transparent text-sm font-semibold focus:outline-none transition-all duration-200",
              "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0",
              error
                ? "border-brand-terracotta-bright text-brand-terracotta-bright"
                : "border-input",
              disabled && "opacity-50 cursor-not-allowed",
            )}
          />
        )}
      </div>

      {/* Type Select */}
      <div className="flex-1 space-y-2">
        <Label
          className={cn(
            "text-sm uppercase",
            error ? "text-brand-terracotta-bright" : "text-primary",
          )}
        >
          Type
        </Label>
        <div className="relative">
          <select
            disabled={disabled}
            {...typeRegistrationProps}
            ref={typeFormRef}
            className={cn(
              "w-full pb-2 pt-1 border-0 border-b rounded-none bg-transparent text-sm font-semibold appearance-none focus:outline-none transition-all duration-200",
              error
                ? "border-brand-terracotta-bright text-brand-terracotta-bright"
                : "border-input",
              disabled && "opacity-50 cursor-not-allowed",
            )}
          >
            {repeatAtTypeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none">
            <ChevronDown className="size-4" />
          </div>
        </div>
      </div>
    </div>
  )
}
